import React, { useState, useEffect, useCallback, useContext, useMemo } from "react";
import axios from "axios";
import { toast } from "@/components/ui/sonner";
import { Search, Plus, Download, Upload, Copy, MoreHorizontal, ChevronLeft, ChevronRight,
  ChevronUp, ChevronDown, Boxes, Package, Truck, Layers, ArchiveX, Receipt, Flag, Filter, RefreshCw, Eye, Check, ArrowDownUp, ArrowLeftRight, PackageCheck } from "lucide-react";
import { AuthContext } from "@/App";
import { StatValue } from "@/components/dashboard/StatCards";
import AttentionChip from "@/components/AttentionChip";
import StockCenterBrowse from "@/components/StockCenterBrowse";
import StockCenterActivity from "@/components/StockCenterActivity";
import StockCenterLiveStrip from "@/components/StockCenterLiveStrip";
import { SellBitModal, ReceiveBitModal, BitDetailModal, BitImportModal, TransferBitModal } from "@/components/StockCenterModals";
import WarehouseFlag, { AllWarehousesGlyph, FLAG_SRC } from "@/components/icons/WarehouseFlag";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// status -> { accent, label, icon }. Accent drives the AttentionChip + row pill.
const STATUS_META = {
  in_stock:   { color: "#0F6E56", label: "In stock",   icon: Package },
  in_transit: { color: "#185FA5", label: "In transit", icon: Truck },
  unfinished: { color: "#854F0B", label: "Unfinished", icon: Layers },
  overstock:  { color: "#534AB7", label: "Overstock",  icon: Boxes },
  sold:       { color: "#5F5E5A", label: "Sold",       icon: Receipt },
  missing:    { color: "#A32D2D", label: "Missing",    icon: ArchiveX },
};
const STATUS_ORDER = ["in_stock", "in_transit", "unfinished", "overstock", "sold", "missing"];
const PAGE_SIZES = [25, 50, 100, 200];

function StatusPill({ status }) {
  const m = STATUS_META[status] || { color: "#5F5E5A", label: status, icon: null };
  const Icon = m.icon;
  // Color + ICON + text → not color-alone, so colourblind users can tell statuses apart.
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full rl-pill text-[11px] font-medium whitespace-nowrap"
          style={{ color: m.color, backgroundColor: `${m.color}14`, border: `1px solid ${m.color}55` }}>
      {Icon ? <Icon className="w-3 h-3 shrink-0" aria-hidden="true" /> : <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: m.color }} />}
      {m.label}
    </span>
  );
}

function csvCell(v) {
  const s = v == null ? "" : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

// Compress the cutter spec into compact lines: "G 9×1208", "P 12×1308",
// "D 3×1313". Only includes the ones present. Returns an array of strings.
function cutterLines(b) {
  const out = [];
  const pair = (qty, size, prefix) => {
    const q = (qty ?? "").toString().trim(), s = (size ?? "").toString().trim();
    if (!q && !s) return null;
    return `${prefix} ${q || "?"}${s ? `×${s}` : ""}`;
  };
  const g = pair(b.gauge_cutter_qty, b.gauge_cutter_size, "G");
  const p = pair(b.pdc_qty, b.pdc_size, "P");
  const d = pair(b.dome_qty, b.dome_size, "D");
  [g, p, d].forEach((x) => x && out.push(x));
  return out;
}

// Friendly short label for the review chip, so staff see WHAT to fix at a glance.
const FLAG_TEXT = { incomplete: "Incomplete", marking_collision: "Dup. marking", blades_review: "Check blades" };
function FLAG_LABEL(flags) {
  const f = (flags || [])[0];
  return FLAG_TEXT[f] || "Review";
}

// Recessed "+" appended to a partial money figure (some bits have no price, so
// the total understates). Hover explains why. Sized relative to its context.
function PartialPlus({ className = "" }) {
  return (
    <span
      className={`font-mono-data text-[hsl(var(--muted-foreground))]/70 cursor-help ${className}`}
      title="Partial — some bits have no price recorded, so this total understates the real value."
      aria-label="partial; some bits have no recorded price"
    >+</span>
  );
}

// Consistent money format across the page: "EUR 1,840" — ISO code + space +
// thousands-separated amount. Used by the hero (single string).
function fmtMoney(amount, ccy) {
  const n = Math.round(Number(String(amount).replace(/,/g, "")) || 0).toLocaleString();
  return ccy ? `${ccy} ${n}` : n;
}

// Column-header filter: label + funnel; opens a dropdown of distinct values.
function ColFilter({ label, value, options, onChange }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="inline-flex items-center gap-1 group/cf py-1 -my-1 px-1 -mx-1 rounded text-xs uppercase tracking-wider font-semibold text-[hsl(var(--muted-foreground))] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0F6E56]"
                data-testid={`colfilter-${label.toLowerCase()}`}
                aria-label={`Filter by ${label}${value ? `, currently ${value}` : ""}`}>
          <span>{label}</span>
          <Filter className={`w-3.5 h-3.5 transition-opacity ${value ? "opacity-100 text-[#0F6E56]" : "opacity-45 group-hover/cf:opacity-80"}`} aria-hidden="true" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="rl-frost border-[hsl(var(--border))] max-h-72 overflow-y-auto w-52">
        <DropdownMenuItem onClick={() => onChange("")} className={!value ? "font-semibold text-[#0F6E56]" : ""}>All</DropdownMenuItem>
        {options.map((o) => (
          <DropdownMenuItem key={o} onClick={() => onChange(o)} className={value === o ? "font-semibold text-[#0F6E56]" : ""}>
            <span className="truncate">{o}</span>
          </DropdownMenuItem>
        ))}
        {options.length === 0 && <DropdownMenuItem disabled>No values</DropdownMenuItem>}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// Clickable sort header. Carries the StagePage header treatment (text-xs
// uppercase tracking-wider font-semibold muted) and shows an up/down chevron:
// muted at rest, accent green + solid when this column is the active sort.
function SortHeader({ label, sortKey, sort, onSort, align = "left", children }) {
  const active = sort.key === sortKey;
  const Chevron = active && sort.dir === "desc" ? ChevronDown : ChevronUp;
  return (
    <button
      type="button"
      onClick={() => onSort(sortKey)}
      data-testid={`sort-${sortKey}`}
      aria-label={`Sort by ${label}${active ? `, ${sort.dir === "asc" ? "ascending" : "descending"}` : ""}`}
      className={`group/sh inline-flex items-center gap-1 py-1 -my-1 px-1 -mx-1 rounded text-xs uppercase tracking-wider font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0F6E56] ${align === "right" ? "flex-row-reverse" : ""} ${active ? "text-[#0F6E56] dark:text-[#5DCAA5]" : "text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]"}`}
    >
      <span>{children || label}</span>
      <Chevron className={`w-3 h-3 shrink-0 transition-opacity ${active ? "opacity-100" : "opacity-0 group-hover/sh:opacity-45"}`} aria-hidden="true" />
    </button>
  );
}

// Decorative masthead backdrop for the table header band. Shows the active
// warehouse's flag anchored at the top-left edge, scaled up and faded, then
// MASKED so it dissolves to nothing BEFORE its own right/bottom edge — the fade
// completes inside the image, so there's no hard rectangle seam. "All
// warehouses" gets a subtle globe instead. Purely decorative (aria-hidden) and
// pointer-events:none so it never intercepts clicks.
function MastheadBackdrop({ activeWh }) {
  const flag = activeWh !== "ALL" ? FLAG_SRC[activeWh] : null;
  // Premium fade: the flag fills a wide band (cover → overflows right, no hard
  // internal edge) anchored top-LEFT showing the identifying canton/left, and a
  // long, multi-stop gradient mask in BOTH axes dissolves it silkily into the
  // card — a soft feathered vignette, not a cropped rectangle. Behind the title;
  // no competing text on this side.
  // Horizontal feather reaches fully transparent by ~30% — right where the flag
  // image ends (~106px in a 360px box) — so the flag's own right edge dissolves
  // softly instead of showing a hard vertical cut.
  // Flag feather: FULL-HEIGHT flag filling the slab (the earlier look that read
  // best — no squeezing). Only a horizontal fade that reaches transparent EARLY
  // (~26%) so the value text picks up right where the flag dissolves (tight gap,
  // like the globe). No vertical feather — that made it look like a floating
  // squeezed band; the flag fills top-to-bottom and the slab's own rounded edge
  // contains it.
  const flagX = "linear-gradient(90deg, rgba(0,0,0,1) 0%, rgba(0,0,0,0.78) 12%, rgba(0,0,0,0.45) 20%, rgba(0,0,0,0.2) 27%, rgba(0,0,0,0.06) 34%, rgba(0,0,0,0) 42%)";
  if (flag) {
    return (
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-y-0 left-0 w-[300px] opacity-[0.16] dark:opacity-[0.20] select-none"
        style={{
          backgroundImage: `url(${flag})`,
          // Full-height flag, vertically centred, flush to the left edge.
          backgroundSize: "auto 100%",
          backgroundPosition: "left center",
          backgroundRepeat: "no-repeat",
          WebkitMaskImage: flagX,
          maskImage: flagX,
        }}
      />
    );
  }
  // "All warehouses" → oversized globe. More present than the flags (it's a thin
  // line glyph, not a filled image): higher opacity + a gentler, later-reaching
  // fade so the globe reads clearly while still dissolving softly to the right.
  const globeFade = "linear-gradient(100deg, rgba(0,0,0,1) 0%, rgba(0,0,0,0.85) 45%, rgba(0,0,0,0.4) 70%, rgba(0,0,0,0) 92%)";
  return (
    <div
      aria-hidden="true"
      className="pointer-events-none absolute -top-9 -left-5 w-[200px] h-[200px] text-[#0F6E56] opacity-[0.22] dark:opacity-[0.26] select-none"
      style={{ WebkitMaskImage: globeFade, maskImage: globeFade }}
    >
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4"
           strokeLinecap="round" strokeLinejoin="round" className="w-full h-full">
        <circle cx="12" cy="12" r="9" /><path d="M3 12h18" />
        <path d="M12 3a14 14 0 0 1 0 18a14 14 0 0 1 0-18" />
      </svg>
    </div>
  );
}

// Skeleton placeholder row — shown while the table is (re)loading, e.g. on a
// country switch, so the data fades in elegantly instead of flashing. A soft
// pulse on muted bars; widths vary per row for a natural look. Column set mirrors
// the live table (warehouse/value gated the same way).
function SkeletonRow({ showWarehouse, showValue, index }) {
  const bar = (w) => (
    <span className="block h-3 rounded bg-[hsl(var(--muted-foreground))]/15" style={{ width: w }} />
  );
  // Deterministic per-row variation (no layout jitter between renders).
  const v = [["62%", "70%"], ["48%", "55%"], ["72%", "60%"]][index % 3];
  return (
    <tr className="border-b border-[hsl(var(--border))]/60 last:border-0 animate-pulse" aria-hidden="true">
      <td className="px-3 py-2.5"><span className="block w-4 h-4 rounded bg-[hsl(var(--muted-foreground))]/15" /></td>
      <td className="px-3 py-2.5"><span className="block h-5 w-[70px] rounded-md bg-[hsl(var(--muted-foreground))]/15" /></td>
      <td className="px-3 py-2.5">{bar(v[0])}</td>
      <td className="px-3 py-2.5">{bar("46px")}</td>
      <td className="px-3 py-2.5">{bar(v[1])}</td>
      <td className="px-3 py-2.5">{bar("64px")}</td>
      <td className="px-3 py-2.5"><span className="block h-5 w-[68px] rounded-full bg-[hsl(var(--muted-foreground))]/15" /></td>
      {showWarehouse && <td className="px-3 py-2.5"><span className="block h-4 w-[52px] rounded bg-[hsl(var(--muted-foreground))]/15" /></td>}
      {showValue && <td className="px-3 py-2.5"><span className="block h-3 w-[58px] rounded bg-[hsl(var(--muted-foreground))]/15 ml-auto" /></td>}
      <td className="px-3 py-2.5"></td>
    </tr>
  );
}

// Row value cell: currency code as a recessed bold unit in a fixed-width gutter
// (so codes line up vertically), number medium-weight + right-aligned. The
// gutter fits up to 99,999; larger numbers naturally push the code left.
function BitValue({ amount, currency }) {
  const n = Math.round(Number(String(amount).replace(/,/g, "")) || 0).toLocaleString();
  return (
    <span className="inline-flex items-baseline justify-end gap-1.5 w-full">
      <span className="font-mono-data text-[10px] font-bold uppercase tracking-wide text-[hsl(var(--muted-foreground))]/70 w-8 text-right shrink-0">{currency || ""}</span>
      <span className="font-mono-data text-[13px] font-medium tabular-nums text-[hsl(var(--foreground))] min-w-[58px] text-right">{n}</span>
    </span>
  );
}

// Mobile (<sm) card for one bit — replaces a too-wide table row on a phone.
// Collapsed: marking pill · size · type · status. Tap to expand details + actions.
function BitCardMobile({ b, canViewCost, canSell, canManage, showWarehouse, onDetail, onSell, onDuplicate, onExportRow }) {
  const [open, setOpen] = useState(false);
  const dash = <span className="text-[hsl(var(--muted-foreground))]/60">–</span>;
  const size = b.bit_size_in || (b.bit_size_mm ? `${b.bit_size_mm} mm` : "");
  const cutters = cutterLines(b);
  const rows = [
    ["Design", b.design_no],
    ["Thread", b.thread_size],
    ["Cutters", cutters.length ? cutters.join("  ") : ""],
    ...(showWarehouse ? [["Warehouse", b.warehouse]] : []),
    ...(canViewCost ? [["Value", b.price ? `${b.currency || ""} ${Math.round(Number(String(b.price).replace(/,/g, "")) || 0).toLocaleString()}`.trim() : ""]] : []),
  ];
  return (
    <div className={b.needs_review ? "bg-[#EF9F27]/[0.05]" : ""} data-testid="bit-card-mobile">
      {/* Collapsed header — tap to expand. */}
      <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open}
        className="w-full flex items-center gap-2.5 px-4 py-3 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#0F6E56]">
        {b.marking_no
          ? <span className="font-mono-data text-[12px] px-2 py-0.5 rounded-md rl-carved bg-[hsl(var(--muted))]/40 font-semibold text-[hsl(var(--foreground))] shrink-0">{b.marking_no}</span>
          : <span className="font-mono-data text-[12px] px-2 py-0.5 rounded-md border border-dashed border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))] opacity-60 shrink-0">no serial</span>}
        <span className="min-w-0 flex-1 text-[13px] text-[hsl(var(--foreground))]/85 truncate">
          {size || dash}{b.blades ? <span className="text-[hsl(var(--muted-foreground))] text-[11px]"> · {b.blades}</span> : null}
        </span>
        <StatusPill status={b.status} />
        <ChevronDown className={`w-4 h-4 text-[hsl(var(--muted-foreground))]/60 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} aria-hidden="true" />
      </button>
      {/* Expanded — details + actions. */}
      {open && (
        <div className="px-4 pb-3 pt-1 bg-[hsl(var(--muted))]/20">
          <div className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 text-[12px] mb-3">
            {rows.map(([label, val]) => (
              <React.Fragment key={label}>
                <span className="text-[hsl(var(--muted-foreground))]">{label}</span>
                <span className="font-mono-data text-right text-[hsl(var(--foreground))]/85 truncate">{val || dash}</span>
              </React.Fragment>
            ))}
            {b.needs_review && (
              <>
                <span className="text-[hsl(var(--muted-foreground))]">Review</span>
                <span className="text-right text-[#854F0B] dark:text-[#FAC775] text-[11px]">{(b.flags || []).join(", ") || "needs review"}</span>
              </>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={onDetail} className="flex-1 inline-flex items-center justify-center gap-1.5 h-9 rounded-full rl-pill border border-[hsl(var(--border))] text-[hsl(var(--foreground))]/80 text-[11px] font-semibold uppercase tracking-[0.05em]" data-testid="card-details">
              <Eye className="w-3.5 h-3.5" /> Details
            </button>
            {canSell && b.status !== "sold" && (
              <button onClick={onSell} className="rl-lit-btn flex-1 inline-flex items-center justify-center gap-1.5 h-9 rounded-full text-white text-[11px] font-bold uppercase tracking-wider" style={{ backgroundColor: "#0F6E56" }} data-testid="card-sell">
                <Receipt className="w-3.5 h-3.5" /> Mark sold
              </button>
            )}
            {canManage && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="rl-well w-9 h-9 rounded-full inline-flex items-center justify-center text-[hsl(var(--muted-foreground))]/70 shrink-0" aria-label="More actions">
                    <MoreHorizontal className="w-4 h-4" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="rl-frost border-[hsl(var(--border))]">
                  <DropdownMenuItem onClick={onDuplicate}><Copy className="w-3.5 h-3.5 mr-2" /> Duplicate</DropdownMenuItem>
                  <DropdownMenuItem onClick={onExportRow}><Download className="w-3.5 h-3.5 mr-2" /> Export row</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>
      )}
    </div>
  );
}


// Step 2 of a transfer: bits sent here that haven't been confirmed as arrived.
// Shows on the destination warehouse view; "Confirm arrival" clears in_transit.
function IncomingTransfers({ activeWh, canTransfer, onChanged }) {
  const [rows, setRows] = useState([]);
  const [busyId, setBusyId] = useState(null);

  const load = React.useCallback(async () => {
    try {
      const res = await axios.get(`${API}/stock-center/bits/in-transit${activeWh !== "ALL" ? `?warehouse=${activeWh}` : ""}`);
      setRows(Array.isArray(res.data) ? res.data : []);
    } catch { setRows([]); }
  }, [activeWh]);
  useEffect(() => { load(); }, [load]);

  const confirm = async (b) => {
    setBusyId(b.id);
    try {
      await axios.post(`${API}/stock-center/bits/${b.id}/confirm-arrival`);
      toast.success(`Arrival confirmed — ${b.marking_no || "bit"} is now in stock`);
      await load(); onChanged?.();
    } catch (err) { toast.error(err?.response?.data?.detail || "Couldn't confirm arrival"); }
    finally { setBusyId(null); }
  };

  if (!rows.length) return null;
  return (
    <div className="mb-3 rounded-xl border border-[#534AB7]/25 bg-[#534AB7]/[0.04] px-3.5 py-2.5">
      <div className="flex items-center gap-2 text-[12.5px] font-medium text-[hsl(var(--foreground))] mb-1.5">
        <PackageCheck className="w-4 h-4 text-[#534AB7]" /> Incoming transfers
        <span className="text-[11px] font-normal text-white bg-[#534AB7] rounded-full px-2 leading-[17px]">{rows.length}</span>
      </div>
      <div className="divide-y divide-[hsl(var(--border))]">
        {rows.map((b) => (
          <div key={b.id} className="flex items-center gap-2.5 py-1.5">
            <span className="w-2 h-2 rounded-full bg-[#534AB7] shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-[12.5px] text-[hsl(var(--foreground))] truncate">
                <span className="font-mono-data">{b.marking_no || "—"}</span>
                {b.blades ? ` · ${b.blades}` : ""}{b.bit_size_in ? ` · ${b.bit_size_in}"` : ""}
              </div>
            </div>
            {canTransfer && (
              <button onClick={() => confirm(b)} disabled={busyId === b.id}
                className="inline-flex items-center gap-1 text-[12px] font-medium px-2.5 py-1 rounded-md text-white disabled:opacity-50 shrink-0" style={{ backgroundColor: "#0F6E56" }}>
                {busyId === b.id ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />} Confirm arrival
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function StockCenterPage() {
  const { user } = useContext(AuthContext);
  const canManage = user?.role === "Admin" || !!user?.permissions?.can_manage_bits;
  const canViewCost = user?.role === "Admin" || !!user?.permissions?.can_view_bit_cost;
  const canTransfer = user?.role === "Admin" || !!user?.permissions?.can_transfer_bits;

  const [warehouses, setWarehouses] = useState([]);
  const [bits, setBits] = useState([]);
  const [summary, setSummary] = useState(null);
  const [valueSummary, setValueSummary] = useState(null);
  const [valueLoading, setValueLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [activeWh, setActiveWh] = useState("ALL");
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [reviewOnly, setReviewOnly] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [selected, setSelected] = useState(new Set());
  const [bulkField, setBulkField] = useState(null);
  const [bulkValue, setBulkValue] = useState("");
  const [modal, setModal] = useState(null); // {type:"receive"} | {type:"sell", bit}
  const [colFilters, setColFilters] = useState({ blades: "", thread_size: "" });
  const [sort, setSort] = useState({ key: "", dir: "asc" }); // "" = unsorted (insertion order)
  const [viewMode, setViewMode] = useState("browse"); // "browse" (product funnel) | "table" (full grid)
  const [whMenuOpen, setWhMenuOpen] = useState(false); // mobile warehouse dropdown open state
  const canSell = user?.role === "Admin" || !!user?.permissions?.can_sell_bits;

  // Click a header to sort by it: 1st click asc, 2nd desc, 3rd clears back to
  // natural order. Mirrors the StagePage DueFilter sort affordance.
  const toggleSort = (key) => {
    setSort((s) => s.key !== key ? { key, dir: "asc" } : s.dir === "asc" ? { key, dir: "desc" } : { key: "", dir: "asc" });
    setPage(1);
  };

  const fetchWarehouses = useCallback(async () => {
    try { const res = await axios.get(`${API}/stock-center/warehouses`); setWarehouses(res.data.warehouses || []); }
    catch (e) { /* surfaced by bits fetch */ }
  }, []);

  const fetchBits = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const params = new URLSearchParams();
      if (activeWh !== "ALL") params.set("warehouse", activeWh);
      if (statusFilter) params.set("status", statusFilter);
      if (reviewOnly) params.set("needs_review", "true");
      const [bitsRes, sumRes] = await Promise.all([
        axios.get(`${API}/stock-center/bits?${params.toString()}`),
        axios.get(`${API}/stock-center/summary${activeWh !== "ALL" ? `?warehouse=${activeWh}` : ""}`),
      ]);
      setBits(bitsRes.data || []); setSummary(sumRes.data || null);
    } catch (e) { setError(e.response?.data?.detail || "Could not load the Stock Center"); }
    finally { setLoading(false); }
  }, [activeWh, statusFilter, reviewOnly]);

  // Value summary tracks the warehouse only (not the row filters) — the CFO
  // wants the whole warehouse's worth regardless of which status is filtered.
  const fetchValueSummary = useCallback(async () => {
    if (!canViewCost) { setValueSummary(null); return; }
    setValueLoading(true);
    try {
      const res = await axios.get(`${API}/stock-center/value-summary${activeWh !== "ALL" ? `?warehouse=${activeWh}` : ""}`);
      setValueSummary(res.data || null);
    } catch (e) { setValueSummary(null); }
    finally { setValueLoading(false); }
  }, [activeWh, canViewCost]);

  useEffect(() => { fetchWarehouses(); }, [fetchWarehouses]);
  useEffect(() => { fetchBits(); setPage(1); setSelected(new Set()); setColFilters({ blades: "", thread_size: "" }); }, [fetchBits]);
  useEffect(() => { fetchValueSummary(); }, [fetchValueSummary]);

  // Fields searched by the global box — ALL meaningful bit properties.
  const SEARCH_FIELDS = useMemo(() => [
    "marking_no", "design_no", "status", "bit_size_in", "bit_size_mm", "body_type",
    "blades", "thread_size", "nozzle_count", "nozzle_size", "gauge_cutter_qty",
    "gauge_cutter_size", "pdc_qty", "pdc_size", "dome_qty", "dome_size", "unit_weight",
    "box_size", "shelf_location", "supplier", "spec_sheet", "customer", "order_no",
    "shipment_no", "remarks", "warehouse",
  ], []);

  // Per-column quick filters (exact value match). "" = no filter on that column.
  const setColFilter = (k, v) => { setColFilters((p) => ({ ...p, [k]: v })); setPage(1); };

  // Distinct values for a column (from the loaded set), for the filter dropdowns.
  const distinctFor = useCallback((k) => {
    const s = new Set();
    bits.forEach((b) => { const v = (b[k] || "").trim(); if (v) s.add(v); });
    return Array.from(s).sort();
  }, [bits]);

  // Distinct-value suggestions per field for inline editing (autocomplete that
  // suggests existing clean values but still allows new ones). Built once from
  // the loaded set; capped per field so a datalist stays snappy.
  const SUGGEST_FIELDS = useMemo(() => [
    "design_no", "body_type", "blades", "thread_size", "nozzle_count", "nozzle_size",
    "gauge_cutter_qty", "gauge_cutter_size", "pdc_qty", "pdc_size", "dome_qty", "dome_size",
    "unit_weight", "box_size", "shelf_location", "supplier", "size_label", "bit_size_in",
    "bit_size_mm", "currency", "customer",
  ], []);
  const suggestions = useMemo(() => {
    const map = {};
    SUGGEST_FIELDS.forEach((k) => {
      const s = new Set();
      bits.forEach((b) => { const v = (b[k] ?? "").toString().trim(); if (v) s.add(v); });
      map[k] = Array.from(s).sort().slice(0, 200);
    });
    return map;
  }, [bits, SUGGEST_FIELDS]);

  const filtered = useMemo(() => {
    let rows = bits;
    const ql = q.trim().toLowerCase();
    if (ql) rows = rows.filter((b) => SEARCH_FIELDS.some((k) => String(b[k] || "").toLowerCase().includes(ql)));
    Object.entries(colFilters).forEach(([k, v]) => {
      if (v) rows = rows.filter((b) => (b[k] || "") === v);
    });
    return rows;
  }, [bits, q, colFilters, SEARCH_FIELDS]);

  // Header-click sort. Numeric-aware: a value that parses as a number sorts
  // numerically (so 14330 < 17789 and 4.75 < 12.25), otherwise locale string
  // compare. Blanks always sink to the bottom regardless of direction.
  const sorted = useMemo(() => {
    if (!sort.key) return filtered;
    const num = (v) => { const n = parseFloat(String(v == null ? "" : v).replace(/[^0-9.+-]/g, "")); return Number.isFinite(n) ? n : null; };
    const dir = sort.dir === "desc" ? -1 : 1;
    return [...filtered].sort((a, b) => {
      const av = a[sort.key], bv = b[sort.key];
      const ae = av == null || av === "", be = bv == null || bv === "";
      if (ae && be) return 0;
      if (ae) return 1;   // blanks sink
      if (be) return -1;
      const an = num(av), bn = num(bv);
      if (an != null && bn != null) return (an - bn) * dir;
      return String(av).localeCompare(String(bv), undefined, { numeric: true }) * dir;
    });
  }, [filtered, sort]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const pageClamped = Math.min(page, totalPages);
  const paged = useMemo(() => sorted.slice((pageClamped - 1) * pageSize, pageClamped * pageSize), [sorted, pageClamped, pageSize]);

  const byStatus = summary?.by_status || {};

  // Sold value for the hero. In a SINGLE warehouse → local currency; across ALL
  // → USD total via the rate table. Trailing "+" = some sold bits had no price.
  const soldValueLine = useCallback(() => {
    if (!canViewCost || !valueSummary) return null;
    const priced = (valueSummary.status_priced || {}).sold || 0;
    if (priced === 0) return null;
    if (activeWh === "ALL") {
      const usd = (valueSummary.status_value_usd || {}).sold;
      return usd == null ? null : fmtMoney(usd, "USD");
    }
    const local = (valueSummary.status_value_local || {}).sold || {};
    const cur = Object.keys(local)[0];
    return cur ? fmtMoney(local[cur], cur) : null;
  }, [canViewCost, valueSummary, activeWh]);

  const toggleSel = useCallback((id) => setSelected((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; }), []);
  const allPageSelected = paged.length > 0 && paged.every((b) => selected.has(b.id));
  const toggleAllPage = () => setSelected((prev) => { const n = new Set(prev); paged.forEach((b) => (allPageSelected ? n.delete(b.id) : n.add(b.id))); return n; });

  const duplicateBit = async (bit) => {
    try { await axios.post(`${API}/stock-center/bits/${bit.id}/duplicate`); toast.success("Bit duplicated — fill in the new serial"); fetchBits(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Duplicate failed"); }
  };

  // Open a bit's detail drawer from an activity row. The feed carries the bit's
  // marking + warehouse; reuse the /bits?q= lookup (same as the funnel) to load
  // the full bit, then open the existing detail modal. Silent on a miss — a
  // deleted bit just won't open.
  const openBitFromActivity = useCallback(async (txn) => {
    const marking = (txn?.marking_no || "").trim();
    if (!marking) return;
    try {
      const wh = txn.warehouse && txn.warehouse !== "ALL" ? `warehouse=${txn.warehouse}&` : "";
      const res = await axios.get(`${API}/stock-center/bits?${wh}q=${encodeURIComponent(marking)}`);
      const bit = (res.data || []).find((b) => String(b.marking_no) === String(marking));
      if (bit) setModal({ type: "detail", bit });
    } catch (e) { /* non-fatal — row just doesn't open */ }
  }, []);
  const bulkApply = async () => {
    if (!bulkField) return;
    try {
      const res = await axios.post(`${API}/stock-center/bits/bulk-update`, { ids: [...selected], changes: { [bulkField]: bulkValue } });
      toast.success(`Updated ${res.data?.updated ?? 0} bit(s)`);
      setBulkField(null); setBulkValue(""); setSelected(new Set()); fetchBits();
    } catch (e) { toast.error(e?.response?.data?.detail || "Bulk update failed"); }
  };
  const bulkDelete = async () => {
    if (!window.confirm(`Delete ${selected.size} selected bit(s)? This cannot be undone.`)) return;
    try { const res = await axios.post(`${API}/stock-center/bits/bulk-delete`, { ids: [...selected] }); toast.success(`Deleted ${res.data?.deleted ?? 0} bit(s)`); setSelected(new Set()); fetchBits(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Bulk delete failed"); }
  };
  const exportCSV = (rows, name) => {
    if (!rows.length) { toast.message("Nothing to export"); return; }
    const cols = ["warehouse", "marking_no", "design_no", "status", "bit_size_in", "bit_size_mm", "body_type", "blades",
      "thread_size", "customer", "supplier", "shelf_location", ...(canViewCost ? ["price", "currency"] : []), "flags"];
    const body = rows.map((b) => cols.map((c) => csvCell(c === "flags" ? (b.flags || []).join(" | ") : b[c])).join(",")).join("\n");
    const blob = new Blob([cols.join(",") + "\n" + body], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob); const a = document.createElement("a");
    a.href = url; a.download = `${name}_${new Date().toISOString().slice(0, 10)}.csv`; a.click(); URL.revokeObjectURL(url);
  };

  // Only the TABLE view depends on the (potentially slow) /bits fetch. Browse
  // mode has its own fast /products fetch + skeleton, so it must NOT be blocked
  // by a full-page spinner/error while bits is loading — otherwise a slow DB
  // hangs the whole page even though Browse needs none of that data.
  if (viewMode === "table" && loading && !bits.length) {
    return <div className="flex items-center justify-center py-20"><div className="w-7 h-7 border-2 border-[#0F6E56]/30 border-t-[#0F6E56] rounded-full animate-spin" /></div>;
  }
  if (viewMode === "table" && error) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 py-20 text-center">
        <div className="w-12 h-12 rounded-full bg-[hsl(var(--destructive))]/10 flex items-center justify-center">
          <ArchiveX className="w-6 h-6 text-[hsl(var(--destructive))]" />
        </div>
        <div>
          <p className="text-[15px] font-medium text-[hsl(var(--foreground))]">{error}</p>
          <p className="text-[12px] text-[hsl(var(--muted-foreground))] mt-1">This is usually a brief connection hiccup. Try again in a moment.</p>
        </div>
        <button onClick={() => { fetchBits(); fetchWarehouses(); fetchValueSummary(); }}
                className="inline-flex items-center gap-1.5 text-[13px] px-4 py-2 rounded-md text-white" style={{ backgroundColor: "#0F6E56" }}>
          <RefreshCw className="w-4 h-4" /> Retry
        </button>
      </div>
    );
  }

  const whTabs = [{ code: "ALL", name: "All warehouses", count: warehouses.reduce((s, w) => s + (w.count || 0), 0) }, ...warehouses];
  // Warehouse column is redundant on a single-country view (every row IS that
  // country) — show it only on "All warehouses".
  const showWarehouseCol = activeWh === "ALL";

  return (
    <div data-testid="stock-center-page">
      {/* ── Warehouse switcher ──
          MOBILE (<sm): a dropdown selector — the swipeable pill rail clipped off-
          screen on a phone, so a tap-to-open list is used instead; the current
          warehouse (flag · name · count) shows at rest, the menu lists them all.
          DESKTOP (sm+): the Safari-style pill rail (below), unchanged. */}
      <div className="sm:hidden mb-3.5">
        <DropdownMenu open={whMenuOpen} onOpenChange={setWhMenuOpen}>
          <DropdownMenuTrigger asChild>
            <button
              data-testid="wh-select-mobile"
              aria-label="Choose warehouse"
              className={`w-full rounded-full h-11 px-4 inline-flex items-center gap-2.5 text-[14px] font-semibold text-[hsl(var(--foreground))] transition-[box-shadow,background-color] duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0F6E56] ${
                whMenuOpen
                  ? "bg-[hsl(var(--muted))]/60 [box-shadow:inset_0_1px_3px_rgba(0,0,0,0.16),inset_0_0_0_1px_rgba(0,0,0,0.06)]"
                  : "rl-thumb bg-[hsl(var(--card))] border border-[hsl(var(--border))]"
              }`}
            >
              {(() => {
                const cur = whTabs.find((w) => w.code === activeWh) || whTabs[0];
                return (
                  <>
                    {/* Fixed-width flag gutter so the name doesn't shift between
                        warehouses with differently-proportioned flags. */}
                    <span className="w-6 flex items-center justify-center shrink-0">
                      {cur.code === "ALL" ? <AllWarehousesGlyph className="opacity-70" /> : <WarehouseFlag code={cur.code} />}
                    </span>
                    <span className="truncate">{cur.name}</span>
                    <span className="rl-carved font-mono-data tabular-nums text-[11px] px-1.5 py-0.5 rounded-full shrink-0"
                          style={{ background: "#0F6E561f", color: "#0F6E56" }}>
                      {(cur.count || 0).toLocaleString()}
                    </span>
                  </>
                );
              })()}
              <ChevronDown className={`w-4 h-4 ml-auto shrink-0 text-[hsl(var(--muted-foreground))] transition-transform duration-200 ${whMenuOpen ? "rotate-180" : ""}`} aria-hidden="true" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="rl-frost border-[hsl(var(--border))] rounded-2xl w-[calc(100vw-2rem)] max-w-[360px] p-1.5">
            {whTabs.map((w) => {
              const active = activeWh === w.code;
              return (
                <DropdownMenuItem key={w.code} onClick={() => setActiveWh(w.code)} data-testid={`wh-opt-${w.code}`}
                  className={`flex items-center gap-3 h-11 px-2.5 rounded-lg cursor-pointer ${active ? "bg-[#0F6E56]/[0.08] focus:bg-[#0F6E56]/[0.12]" : ""}`}>
                  {/* Fixed-width flag gutter so every name starts at the same x. */}
                  <span className="w-6 flex items-center justify-center shrink-0">
                    {w.code === "ALL" ? <AllWarehousesGlyph className="opacity-70" /> : <WarehouseFlag code={w.code} />}
                  </span>
                  <span className={`text-[14px] truncate ${active ? "font-semibold text-[hsl(var(--foreground))]" : "font-medium text-[hsl(var(--foreground))]/90"}`}>{w.name}</span>
                  {/* Count chip — same vocabulary as the trigger: green tint on the
                      active row, neutral carved chip otherwise. */}
                  <span className="rl-carved font-mono-data tabular-nums text-[11px] px-1.5 py-0.5 rounded-full ml-auto shrink-0"
                        style={active ? { background: "#0F6E561f", color: "#0F6E56" } : { background: "hsl(var(--muted) / 0.7)", color: "hsl(var(--muted-foreground))" }}>
                    {(w.count || 0).toLocaleString()}
                  </span>
                  {/* Reserve the check column on every row so names/counts don't
                      shift between active and inactive rows. */}
                  <span className="w-4 shrink-0">{active && <Check className="w-4 h-4 text-[#0F6E56]" aria-hidden="true" />}</span>
                </DropdownMenuItem>
              );
            })}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* ── Warehouse switcher: Safari-style pills in a recessed pill rail
          (DESKTOP, sm+ only) ──
          The rail is a fully-rounded (pill) recessed track; each tab is a pill.
          The SELECTED tab lifts as a solid rl-thumb pill. Faint hairline
          separators sit between adjacent INACTIVE tabs (Safari style) and vanish
          next to the active pill. Depth = rail inset shadow (no clipped outer
          shadows). */}
      <div className="wh-tabrail hidden sm:inline-flex rl-carved bg-[hsl(var(--muted))]/50 rounded-full p-1 mb-3.5 max-w-full items-center gap-0.5 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden border border-[hsl(var(--border))]">
        {whTabs.map((w, i) => {
          const active = activeWh === w.code;
          const prevActive = i > 0 && activeWh === whTabs[i - 1].code;
          // Separator before this tab, unless it/its neighbour is active or it's first
          const showSep = i > 0 && !active && !prevActive;
          return (
            <React.Fragment key={w.code}>
              {showSep && <span className="wh-sep w-px h-4 bg-[hsl(var(--border))]/70 shrink-0 transition-opacity" aria-hidden="true" />}
              <button
                onClick={() => setActiveWh(w.code)}
                data-testid={`wh-tab-${w.code}`}
                aria-pressed={active}
                aria-label={`${w.name}, ${(w.count || 0).toLocaleString()} bits`}
                className={`wh-tab flex items-center gap-2 px-3 h-9 rounded-full text-[13px] font-semibold whitespace-nowrap shrink-0 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0F6E56] focus-visible:ring-offset-1 ${
                  active
                    ? "rl-thumb bg-[hsl(var(--card))] text-[hsl(var(--foreground))]"
                    : "text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] hover:bg-[hsl(var(--foreground))]/[0.055] active:bg-[hsl(var(--foreground))]/[0.09]"
                }`}
              >
                {w.code === "ALL" ? <AllWarehousesGlyph className="opacity-70" /> : <WarehouseFlag code={w.code} />}
                <span>{w.name}</span>
                <span className="rl-carved font-mono-data tabular-nums text-[11px] px-1.5 py-0.5 rounded-full"
                      style={active ? { background: "#0F6E561f", color: "#0F6E56" } : { background: "hsl(var(--muted) / 0.7)", color: "hsl(var(--muted-foreground))" }}>
                  {(w.count || 0).toLocaleString()}
                </span>
              </button>
            </React.Fragment>
          );
        })}
      </div>

      {/* ── Value hero — the calm financial headline. ALL → one consolidated USD
          total (via the rate table); a single warehouse → its local currency.
          Hidden for users without cost permission. ── */}
      {(() => {
        if (!canViewCost || !valueSummary) return null;
        const isAll = activeWh === "ALL";
        const activeCcy = valueSummary.active_value_by_currency || {};
        const localCur = Object.keys(activeCcy)[0];
        const headlineLabel = isAll ? "Total stock value" : "Stock value";
        const headlineValue = isAll
          ? fmtMoney(valueSummary.active_value_usd || 0, "USD")
          : (localCur ? fmtMoney(activeCcy[localCur], localCur) : "—");
        const subLabel = isAll
          ? `Consolidated to USD across all warehouses · ${valueSummary.priced_pct}% priced`
          : `On-hand carrying value · ${valueSummary.priced_pct}% priced`;
        // Partial flags: headline understates if not all priced; sold likewise.
        const headlinePartial = (valueSummary.priced || 0) < (valueSummary.total || 0);
        const soldVal = soldValueLine();
        const soldPartial = ((valueSummary.status_priced || {}).sold || 0) < ((valueSummary.status_count || {}).sold || 0);
        return (
          <div className="relative overflow-hidden rl-glass rl-hero-glass rounded-2xl px-6 py-5 mb-4" data-testid="sc-hero">
            {/* Faded country flag (or globe for ALL) on the slab's LEFT EDGE. The
                content below is indented (pl) so the value/label text sits clear
                of the flag — plenty of empty slab to the right. */}
            <MastheadBackdrop activeWh={activeWh} />
            {/* While the value summary refreshes (e.g. on a warehouse switch),
                the figures are replaced by neutral skeleton bars — no stale
                numbers shown — matching the Browse skeleton's loading language. */}
            {valueLoading ? (
              <div className="relative z-10 flex items-end justify-between gap-6 flex-wrap pl-[90px] sm:pl-[100px] animate-pulse" aria-hidden="true">
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-[0.1em] text-[hsl(var(--muted-foreground))]/70 mb-1.5">{headlineLabel}</div>
                  <div className="h-[30px] w-[230px] rounded-md bg-[hsl(var(--muted-foreground))]/15" />
                  <div className="h-[10px] w-[150px] rounded bg-[hsl(var(--muted-foreground))]/12 mt-3" />
                </div>
                <div className="flex items-end gap-8">
                  <div className="text-right">
                    <div className="text-[10px] font-bold uppercase tracking-[0.1em] text-[hsl(var(--muted-foreground))]/70 mb-1.5">Sold</div>
                    <div className="h-[20px] w-[96px] rounded bg-[hsl(var(--muted-foreground))]/15 ml-auto" />
                  </div>
                  <div className="text-right">
                    <div className="text-[10px] font-bold uppercase tracking-[0.1em] text-[hsl(var(--muted-foreground))]/70 mb-1.5">Priced coverage</div>
                    <div className="h-[20px] w-[54px] rounded bg-[hsl(var(--muted-foreground))]/15 ml-auto" />
                  </div>
                </div>
              </div>
            ) : (
            <div className="relative z-10 flex items-end justify-between gap-6 flex-wrap pl-[90px] sm:pl-[100px]">
              {/* Stock value — the headline */}
              <div>
                <div className="text-[10px] font-bold uppercase tracking-[0.1em] text-[hsl(var(--muted-foreground))]/70 mb-1.5">{headlineLabel}</div>
                <div className="font-mono-data font-extrabold tabular-nums text-[34px] leading-none text-[hsl(var(--foreground))]" style={{ letterSpacing: "-0.02em" }} data-testid="sc-hero-value">
                  {headlineValue}{headlinePartial && <PartialPlus />}
                </div>
                <div className="text-[11px] text-[hsl(var(--muted-foreground))] mt-2">{subLabel}</div>
              </div>
              <div className="flex items-end gap-8">
                {/* Sold value — secondary money figure */}
                {soldVal && (
                  <div className="text-right">
                    <div className="text-[10px] font-bold uppercase tracking-[0.1em] text-[hsl(var(--muted-foreground))]/70 mb-1.5">Sold {isAll ? "· USD" : ""}</div>
                    <div className="font-mono-data font-bold tabular-nums text-[22px] leading-none text-[#5F5E5A] dark:text-[hsl(var(--muted-foreground))]" data-testid="sc-hero-sold">{soldVal}{soldPartial && <PartialPlus />}</div>
                  </div>
                )}
                {/* Priced coverage — quiet tertiary stat */}
                <div className="text-right">
                  <div className="text-[10px] font-bold uppercase tracking-[0.1em] text-[hsl(var(--muted-foreground))]/70 mb-1.5">Priced coverage</div>
                  <div className="font-mono-data font-bold tabular-nums text-[22px] leading-none text-[hsl(var(--muted-foreground))]">{valueSummary.priced_pct}%</div>
                </div>
              </div>
            </div>
            )}
          </div>
        );
      })()}

      {/* ── Table card ── */}
      <div className="rl-glass-edge rounded-xl overflow-hidden bg-[hsl(var(--card))]">
        {/* Header band (no flag here — it lived behind the title and hurt
            readability; it now sits on the hero value slab's left edge instead). */}
        <div className="px-4 pt-3.5 pb-3 border-b border-[hsl(var(--border))]">
          <div className="flex items-center justify-between gap-3 flex-wrap mb-2.5">
            <div className="flex items-baseline gap-2.5">
              <h2 className="text-[16px] font-bold text-[hsl(var(--foreground))]">
                {activeWh === "ALL" ? "Stock" : (warehouses.find((w) => w.code === activeWh)?.name || "Stock")}
              </h2>
            </div>
            {/* VIEW controls (toggle + search) and ACTIONS (Receive + "⋯"),
                separated by a hairline divider. All controls are a uniform h-8.
                Receive is the only visible action pill; the less-used Import/Export
                live in the "⋯" overflow at every breakpoint.
                MOBILE (<sm): the cluster takes its own full-width row under the
                title and splits into two rows — toggle + Receive + "⋯" on top,
                search full-width below (via flex order). From sm up it's a single
                inline row: toggle · search · divider · Receive · "⋯". */}
            <div className="flex items-center gap-x-3 gap-y-2 w-full sm:w-auto flex-wrap sm:flex-nowrap">
              {/* Browse ↔ Table — carved track + lifted thumb, canonical RockLink segmented control. */}
              <div className="inline-flex items-center h-8 rl-carved bg-[hsl(var(--muted))]/50 rounded-full p-[3px] shrink-0 order-1" role="tablist" aria-label="View mode">
                {[["browse", "Browse"], ["table", "Table"], ["activity", "Activity"]].map(([m, label]) => (
                  <button key={m} role="tab" aria-selected={viewMode === m}
                    onClick={() => setViewMode(m)} data-testid={`sc-view-${m}`}
                    className={`h-[26px] px-3 rounded-full text-[11px] font-semibold uppercase tracking-[0.05em] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0F6E56] ${
                      viewMode === m ? "rl-thumb bg-[hsl(var(--card))] text-[hsl(var(--foreground))]" : "text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]"
                    }`}>{label}</button>
                ))}
              </div>

              {/* Search — full-width own row on mobile (order-last), inline 230px
                  between toggle and divider on sm+ (order-2). */}
              <div className="group/search relative w-full order-last sm:w-[230px] sm:order-2">
                <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-[hsl(var(--muted-foreground))] group-focus-within/search:text-[#0F6E56] transition-colors" />
                <input value={q} onChange={(e) => { setQ(e.target.value); setPage(1); if (e.target.value) setViewMode("table"); }}
                  placeholder="Search any field — marking, thread, cutter size…"
                  aria-label="Search bits across all fields"
                  className="h-8 pl-8 pr-3 text-xs rounded-full rl-search bg-[hsl(var(--muted))]/50 border border-transparent focus:bg-[hsl(var(--card))] focus:border-[#0F6E56]/50 text-[hsl(var(--foreground))] placeholder:text-[hsl(var(--muted-foreground))]/60 focus:outline-none w-full" data-testid="bit-search" />
              </div>

              {/* Divider between view controls and actions (sm+ only — on mobile
                  the rows already separate the groups). */}
              {canManage && <span className="hidden sm:inline-block h-5 w-px bg-[hsl(var(--border))] shrink-0 order-3" aria-hidden="true" />}

              {/* Action group — Receive is the one prominent primary action;
                  the less-used Import/Export live in the "⋯" overflow at BOTH
                  breakpoints (consistent vocabulary, calmer toolbar). Pushed right
                  on mobile (ml-auto), natural position on sm+. */}
              <div className="flex items-center gap-1.5 shrink-0 ml-auto sm:ml-0 order-2 sm:order-4">
                {canManage && (
                  <button onClick={() => setModal({ type: "receive" })} data-testid="receive-bit-btn"
                    className="rl-lit-btn flex items-center gap-1.5 px-3.5 h-8 rounded-full text-white text-[11px] font-semibold uppercase tracking-[0.05em] hover:brightness-110 transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0F6E56] focus-visible:ring-offset-1" style={{ backgroundColor: "#0F6E56" }}>
                    <Plus className="w-3.5 h-3.5" aria-hidden="true" /> Receive
                  </button>
                )}
                {/* Import / Export — a circular glass disc (rl-eye-btn, the
                    canonical disc-at-rest, same as the Browse back buttons). The
                    up/down arrows hint at the data-in / data-out actions inside. */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="rl-eye-btn w-8 h-8 rounded-full inline-flex items-center justify-center text-[hsl(var(--foreground))]/70 hover:text-[hsl(var(--foreground))] active:scale-95 transition-transform focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0F6E56]"
                      aria-label="Import or export data" data-testid="sc-actions-overflow">
                      <ArrowDownUp className="w-4 h-4" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="rl-frost border-[hsl(var(--border))]">
                    {canManage && (
                      <DropdownMenuItem onClick={() => setModal({ type: "import" })} data-testid="sc-overflow-import">
                        <Upload className="w-3.5 h-3.5 mr-2" /> Import bits
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem onClick={() => exportCSV(filtered, `stock_center_${activeWh.toLowerCase()}`)} data-testid="sc-overflow-export">
                      <Download className="w-3.5 h-3.5 mr-2" /> Export view
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          </div>

          {/* Status filters (Table mode only — they filter the grid). StagePage's
              AttentionChip vocabulary: lifted rl-tile glass, rl-ico-well bubble,
              filter-glint when active, and a "STATUS" rail label. */}
          {viewMode === "table" && (
          <div className="flex items-center gap-x-2 gap-y-2 flex-nowrap overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden md:flex-wrap md:overflow-visible">
            <span className="hidden md:inline-block md:w-20 shrink-0 text-[10px] font-bold uppercase tracking-[0.16em] text-[hsl(var(--muted-foreground))]/80 select-none" aria-hidden="true">
              Status
            </span>
            <AttentionChip kind="sc-all" label="All" icon={Boxes} count={filtered.length.toLocaleString()} color="#0F6E56"
                     active={!statusFilter && !reviewOnly} trailing="filter" size="sm" testId="chip-all"
                     onClick={() => { setStatusFilter(""); setReviewOnly(false); }} />
            {STATUS_ORDER.filter((s) => byStatus[s]).map((s) => (
              <AttentionChip key={s} kind={`sc-${s}`} label={STATUS_META[s].label} icon={STATUS_META[s].icon}
                       count={(byStatus[s] || 0).toLocaleString()} color={STATUS_META[s].color}
                       active={statusFilter === s} trailing="filter" size="sm" testId={`chip-${s}`}
                       onClick={() => { setStatusFilter(statusFilter === s ? "" : s); setReviewOnly(false); }} />
            ))}
            {!!summary?.needs_review && (
              <>
                <span className="w-px self-center h-7 bg-[hsl(var(--border))] mx-0.5 shrink-0" aria-hidden="true" />
                <AttentionChip kind="sc-review" label="Needs review" icon={Flag} count={summary.needs_review.toLocaleString()} color="#BA7517"
                         active={reviewOnly} severe={reviewOnly} trailing="filter" size="sm" testId="chip-review"
                         onClick={() => { setReviewOnly(!reviewOnly); setStatusFilter(""); }} />
              </>
            )}
          </div>
          )}

          {/* Incoming transfers — destination's "confirm arrival" queue. Hidden
              when empty; shows in every view mode so a landed bit is never missed. */}
          <div className="px-1 pt-1">
            <IncomingTransfers activeWh={activeWh} canTransfer={canTransfer}
              onChanged={() => { fetchBits(); fetchWarehouses(); fetchValueSummary(); }} />
          </div>

          {/* Browse mode — the product funnel lives in the card body below the
              header band. Rendered here as a sibling so the header (title, search,
              toggle) stays shared across both modes. */}
          {viewMode === "browse" && (
            <div className="pt-1">
              {/* Live activity strip — quiet, ambient "what just happened" line
                  above the funnel; expands to recent events, links to the tab. */}
              <StockCenterLiveStrip activeWh={activeWh}
                onViewAll={() => setViewMode("activity")} onOpenBit={openBitFromActivity} />
              <StockCenterBrowse activeWh={activeWh} canViewCost={canViewCost} canSell={canSell} canManage={canManage} />
            </div>
          )}

          {/* Activity mode — the "what changed, by whom, when" ledger. A row
              click looks the bit up by marking (reusing the /bits?q= lookup) and
              opens its detail drawer. */}
          {viewMode === "activity" && (
            <div className="pt-1">
              <StockCenterActivity activeWh={activeWh} onOpenBit={openBitFromActivity} />
            </div>
          )}
        </div>

        {/* ── Table mode body: bulk bar + full grid + pagination ── */}
        {viewMode === "table" && <>
        {/* Bulk-action bar — FIXED-HEIGHT SLOT: always rendered, toggles
            opacity/pointer-events so the table never reflows when it appears. */}
        <div
          className="overflow-hidden border-b border-[hsl(var(--border))] transition-[height,opacity] duration-150"
          style={{ height: selected.size > 0 ? 40 : 0, opacity: selected.size > 0 ? 1 : 0 }}
          aria-hidden={selected.size === 0}
        >
          <div className="flex items-center gap-2 px-4 h-10 bg-[#185FA5]/[0.06] flex-nowrap overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            <span className="text-[12px] font-semibold text-[#185FA5] dark:text-[#85B7EB] shrink-0">
              <span className="font-mono-data">{selected.size}</span> selected
            </span>
            {canManage && !bulkField && (
              <>
                <button className="text-[12px] px-2 py-1 rounded border border-[hsl(var(--border))] bg-[hsl(var(--card))] shrink-0" onClick={() => setBulkField("status")}>Set status…</button>
                <button className="text-[12px] px-2 py-1 rounded border border-[hsl(var(--border))] bg-[hsl(var(--card))] shrink-0" onClick={() => setBulkField("shelf_location")}>Set location…</button>
                <button className="text-[12px] px-2 py-1 rounded border border-[hsl(var(--border))] bg-[hsl(var(--card))] shrink-0" onClick={() => setBulkField("customer")}>Set customer…</button>
              </>
            )}
            {bulkField && (
              <div className="flex items-center gap-1.5 shrink-0">
                {bulkField === "status" ? (
                  <select value={bulkValue} onChange={(e) => setBulkValue(e.target.value)} className="h-7 px-2 rounded border border-[hsl(var(--border))] bg-[hsl(var(--card))] text-[12px]">
                    <option value="">Choose…</option>
                    {STATUS_ORDER.map((s) => <option key={s} value={s}>{STATUS_META[s].label}</option>)}
                  </select>
                ) : (
                  <input value={bulkValue} onChange={(e) => setBulkValue(e.target.value)} placeholder={bulkField.replace(/_/g, " ")} className="h-7 px-2 rounded border border-[hsl(var(--border))] bg-[hsl(var(--card))] text-[12px] w-36" />
                )}
                <button className="text-[12px] px-2 py-1 rounded text-white" style={{ backgroundColor: "#0F6E56" }} onClick={bulkApply}>Apply</button>
                <button className="text-[12px] px-2 py-1 rounded border border-[hsl(var(--border))]" onClick={() => { setBulkField(null); setBulkValue(""); }}>Cancel</button>
              </div>
            )}
            <div className="flex-1 min-w-[8px]" />
            <button className="text-[12px] px-2 py-1 rounded border border-[hsl(var(--border))] bg-[hsl(var(--card))] shrink-0" onClick={() => exportCSV(bits.filter((b) => selected.has(b.id)), "stock_center_selected")}>Export selected</button>
            {canManage && <button className="text-[12px] px-2 py-1 rounded border border-[hsl(var(--destructive))] text-[hsl(var(--destructive))] shrink-0" onClick={bulkDelete}>Delete</button>}
            <button className="text-[12px] px-2 py-1 rounded text-[hsl(var(--muted-foreground))] shrink-0" onClick={() => setSelected(new Set())}>Clear</button>
          </div>
        </div>

        {/* Table — DESKTOP (sm+). On mobile this wide table is replaced by the
            tap-to-expand card list below (sm:hidden). */}
        <div className="hidden sm:block overflow-x-auto">
          <table className="w-full border-collapse text-[13px]" style={{ minWidth: 880 }}>
            <colgroup>
              <col style={{ width: 38 }} />
              <col style={{ width: 138 }} />
              <col style={{ width: 150 }} />
              <col style={{ width: 92 }} />
              <col style={{ width: 122 }} />
              <col style={{ width: 118 }} />
              <col style={{ width: 102 }} />
              {showWarehouseCol && <col style={{ width: 96 }} />}
              {canViewCost && <col style={{ width: 126 }} />}
              <col style={{ width: 132 }} />
            </colgroup>
            <thead>
              {/* StagePage header DNA: recessed rl-thead band, text-xs uppercase
                  tracking-wider font-semibold muted, with the Part#-pill treatment
                  on the identity column. */}
              {/* Header padding (px-3) matches the body cells (px-3) so titles sit
                  flush over their column. Most titles are click-to-sort.
                  No border-t here: the status band above already draws its
                  border-b, so a border-t would double the line at that seam. */}
              <tr className="rl-thead border-b border-[hsl(var(--border))]">
                <th className="px-3 py-2.5 text-left"><input type="checkbox" checked={allPageSelected} onChange={toggleAllPage} aria-label="Select page" /></th>
                <th className="text-left px-3 py-2.5 whitespace-nowrap">
                  <button type="button" onClick={() => toggleSort("marking_no")} data-testid="sort-marking_no"
                    aria-label={`Sort by marking${sort.key === "marking_no" ? `, ${sort.dir === "asc" ? "ascending" : "descending"}` : ""}`}
                    className="group/sh inline-flex items-center gap-1.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0F6E56] rounded">
                    <span className="font-mono-data text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-md bg-[hsl(var(--card))] border border-[hsl(var(--border))] rl-pill font-semibold text-[hsl(var(--foreground))]">Marking</span>
                    {(() => { const C = sort.key === "marking_no" && sort.dir === "desc" ? ChevronDown : ChevronUp; return <C className={`w-3 h-3 shrink-0 transition-opacity ${sort.key === "marking_no" ? "opacity-100 text-[#0F6E56] dark:text-[#5DCAA5]" : "opacity-0 group-hover/sh:opacity-45 text-[hsl(var(--muted-foreground))]"}`} aria-hidden="true" />; })()}
                  </button>
                </th>
                <th className="text-left px-3 py-2.5">
                  <SortHeader label="Design" sortKey="design_no" sort={sort} onSort={toggleSort} />
                </th>
                <th className="text-left px-3 py-2.5">
                  <SortHeader label="Size" sortKey="bit_size_in" sort={sort} onSort={toggleSort} />
                </th>
                <th className="text-left px-3 py-2.5 text-xs uppercase tracking-wider font-semibold text-[hsl(var(--muted-foreground))]">
                  <ColFilter label="Blades" value={colFilters.blades} options={distinctFor("blades")} onChange={(v) => setColFilter("blades", v)} />
                </th>
                <th className="text-left px-3 py-2.5 text-xs uppercase tracking-wider font-semibold text-[hsl(var(--muted-foreground))]">Cutters</th>
                <th className="text-left px-3 py-2.5">
                  <SortHeader label="Status" sortKey="status" sort={sort} onSort={toggleSort} />
                </th>
                {showWarehouseCol && (
                  <th className="text-left px-3 py-2.5">
                    <SortHeader label="Warehouse" sortKey="warehouse" sort={sort} onSort={toggleSort} />
                  </th>
                )}
                {canViewCost && (
                  <th className="text-right px-3 py-2.5">
                    <SortHeader label="Value" sortKey="price" sort={sort} onSort={toggleSort} align="right" />
                  </th>
                )}
                <th className="text-right px-3 py-2.5 text-xs uppercase tracking-wider font-semibold text-[hsl(var(--muted-foreground))]">Actions</th>
              </tr>
            </thead>
            <tbody className={loading ? "transition-opacity duration-200" : "transition-opacity duration-200"}>
              {/* Country-switch loading → elegant skeleton shimmer rows in place
                  of the data (the table chrome stays, so no layout flash). */}
              {loading ? (
                Array.from({ length: Math.min(Math.max(paged.length || 8, 6), 12) }).map((_, i) => (
                  <SkeletonRow key={`sk-${i}`} showWarehouse={showWarehouseCol} showValue={canViewCost} index={i} />
                ))
              ) : paged.map((b) => {
                const flagged = b.needs_review;
                const isSel = selected.has(b.id);
                const dash = <span className="text-[hsl(var(--muted-foreground))]/60" aria-label="not set">–</span>;
                return (
                  <tr key={b.id} data-testid="bit-row" title="Open full detail"
                      onClick={() => setModal({ type: "detail", bit: b })}
                      onKeyDown={(e) => { if (e.key === "Enter") setModal({ type: "detail", bit: b }); }}
                      tabIndex={0} role="button"
                      aria-label={`Bit ${b.marking_no || "no serial"}, ${b.design_no || ""}, ${(STATUS_META[b.status]?.label || b.status)}, ${b.warehouse}. Press Enter for full detail.`}
                      className={`group border-b border-[hsl(var(--border))] last:border-0 cursor-pointer transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#0F6E56] ${isSel ? "bg-[#185FA5]/[0.07]" : flagged ? "bg-[#EF9F27]/[0.06] hover:bg-[#EF9F27]/[0.1]" : "hover:bg-[hsl(var(--muted))]/30"}`}>
                    {/* Checkbox cell selects; clicking the rest of the row opens detail.
                        Larger hit area + a real keyboard-focusable checkbox. */}
                    <td className="px-2 py-1.5 align-top">
                      <label className="flex items-center justify-center w-7 h-7 cursor-pointer" onClick={(e) => e.stopPropagation()}>
                        <input type="checkbox" checked={isSel} onChange={() => toggleSel(b.id)}
                               className="w-4 h-4 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0F6E56] rounded"
                               aria-label={`Select bit ${b.marking_no || "no serial"}`} />
                      </label>
                    </td>
                    {/* Marking → identity chip + body type recessed below */}
                    <td className="px-3 py-1.5 align-top">
                      <div className="flex flex-col gap-0.5">
                        <span className="inline-flex items-center gap-1.5">
                          {b.marking_no
                            ? <span className="font-mono-data text-[12px] px-2 py-0.5 rounded-md rl-carved bg-[hsl(var(--muted))]/40 font-semibold text-[hsl(var(--foreground))]">{b.marking_no}</span>
                            : <span className="font-mono-data text-[12px] px-2 py-0.5 rounded-md border border-dashed border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))] opacity-60">no serial</span>}
                          {flagged && (
                            <span title={(b.flags || []).join(", ")}
                                  className="inline-flex items-center gap-1 text-[9px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-[#FAEEDA] text-[#854F0B] dark:bg-[#633806]/40 dark:text-[#FAC775] shrink-0">
                              <Flag className="w-2.5 h-2.5" /> {FLAG_LABEL(b.flags)}
                            </span>
                          )}
                        </span>
                        {b.body_type && <span className="text-[10.5px] text-[hsl(var(--muted-foreground))] truncate pl-0.5">{b.body_type}</span>}
                      </div>
                    </td>
                    {/* Design → recessed code chip + type/label below */}
                    <td className="px-3 py-1.5 align-top">
                      <div className="flex flex-col gap-0.5 items-start">
                        {b.design_no
                          ? <span className="font-mono-data text-[11px] px-1.5 py-0.5 rounded-md rl-carved bg-[hsl(var(--muted))]/50 text-[hsl(var(--muted-foreground))]">{b.design_no}</span>
                          : dash}
                        {b.size_label && <span className="text-[10.5px] text-[hsl(var(--muted-foreground))] truncate pl-0.5">{b.size_label}</span>}
                      </div>
                    </td>
                    {/* Size → inches primary + mm recessed */}
                    <td className="px-3 py-1.5 align-top">
                      <div className="flex flex-col gap-0.5">
                        <span className="font-mono-data text-[12px] text-[hsl(var(--foreground))]/85">{b.bit_size_in || (b.bit_size_mm ? `${b.bit_size_mm} mm` : dash)}</span>
                        {b.bit_size_in && b.bit_size_mm && <span className="font-mono-data text-[10.5px] text-[hsl(var(--muted-foreground))]">{b.bit_size_mm} mm</span>}
                      </div>
                    </td>
                    {/* Blades → blades primary + thread recessed (Thread col folded in) */}
                    <td className="px-3 py-1.5 align-top">
                      <div className="flex flex-col gap-0.5">
                        <span className="text-[12px] text-[hsl(var(--foreground))]/85">{b.blades || dash}</span>
                        {b.thread_size && <span className="font-mono-data text-[10.5px] text-[hsl(var(--muted-foreground))] truncate">{b.thread_size}</span>}
                      </div>
                    </td>
                    {/* Cutters → compressed gauge / PDC / dome lines */}
                    <td className="px-3 py-1.5 align-top">
                      {(() => {
                        const lines = cutterLines(b);
                        if (!lines.length) return dash;
                        return (
                          <div className="flex flex-col gap-0.5 font-mono-data text-[10.5px] text-[hsl(var(--muted-foreground))]"
                               title={`Cutters — ${lines.join(", ")}`}
                               aria-label={`Cutters: ${lines.join(", ").replace(/G /g, "Gauge ").replace(/P /g, "PDC ").replace(/D /g, "Dome ").replace(/×/g, " by ")}`}>
                            {lines.map((l, i) => <span key={i} className={i === 0 ? "text-[hsl(var(--foreground))]/90" : ""}>{l}</span>)}
                          </div>
                        );
                      })()}
                    </td>
                    <td className="px-3 py-1.5 align-top"><StatusPill status={b.status} /></td>
                    {showWarehouseCol && (
                      <td className="px-3 py-1.5 align-top">
                        <span className="inline-flex items-center gap-1.5"><WarehouseFlag code={b.warehouse} aria-hidden="true" /><span className="text-[12px] font-medium text-[hsl(var(--foreground))]/80">{b.warehouse}</span></span>
                      </td>
                    )}
                    {canViewCost && (
                      <td className="px-3 py-1.5 text-right align-top">{b.price ? <BitValue amount={b.price} currency={b.currency} /> : dash}</td>
                    )}
                    {/* Actions — StagePage vocabulary: hover-reveal rl-eye-btn
                        Details disc + a lit rl-lit-btn primary pill ("Mark sold",
                        the row's ADVANCE→ equivalent) + an overflow for the rest.
                        Hover-reveal uses opacity/pointer-events on a fixed slot so
                        the row never reflows (RockLink hover-reveal recipe).
                        ON TOUCH (no hover) the actions are ALWAYS visible —
                        [@media(hover:none)] — so phone users aren't dead-ended;
                        pointer devices keep the calm hover-reveal. */}
                    <td className="px-2 py-1.5 align-top" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-end gap-1.5">
                        {/* Details — always rendered, faded until row hover/focus (or always on touch). */}
                        <TooltipProvider delayDuration={0} skipDelayDuration={0}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <button
                                onClick={() => setModal({ type: "detail", bit: b })}
                                className="inline-flex items-center justify-center w-[30px] h-[30px] rounded-full rl-eye-btn text-[hsl(var(--foreground))]/70 hover:text-[hsl(var(--foreground))] active:scale-95 opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto group-focus-within:opacity-100 group-focus-within:pointer-events-auto focus-visible:opacity-100 focus-visible:pointer-events-auto [@media(hover:none)]:opacity-100 [@media(hover:none)]:pointer-events-auto focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/40"
                                aria-label="Open bit details"
                                data-testid={`details-btn-${b.id}`}
                              >
                                <Eye className="w-4 h-4" />
                              </button>
                            </TooltipTrigger>
                            <TooltipContent side="top" sideOffset={6}
                              className="bg-[hsl(var(--muted))] text-[hsl(var(--foreground))] border border-[hsl(var(--border))] shadow-sm text-[10px] font-medium tracking-wide px-2 py-1">
                              Details &amp; history
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>

                        {/* Primary action — lit "Mark sold" pill when sellable.
                            Hover-reveal (same fixed-slot opacity/pointer-events
                            recipe as the eye disc) so a long warehouse list stays
                            calm at rest and the pill appears on row hover/focus. */}
                        {canSell && b.status !== "sold" && (
                          <button
                            onClick={() => setModal({ type: "sell", bit: b })}
                            className="rl-lit-btn inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider whitespace-nowrap text-white hover:brightness-110 transition-[filter,box-shadow,opacity] opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto group-focus-within:opacity-100 group-focus-within:pointer-events-auto focus-visible:opacity-100 focus-visible:pointer-events-auto [@media(hover:none)]:opacity-100 [@media(hover:none)]:pointer-events-auto focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0F6E56]"
                            style={{ backgroundColor: "#0F6E56" }}
                            aria-label={`Mark bit ${b.marking_no || "no serial"} sold`}
                            data-testid={`sell-btn-${b.id}`}
                          >
                            <Receipt className="w-3 h-3" /> <span>Mark sold</span>
                          </button>
                        )}

                        {/* Overflow — duplicate / export (and sell fallback). */}
                        {canManage && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <button className="rl-well w-7 h-7 rounded-full inline-flex items-center justify-center text-[hsl(var(--muted-foreground))]/70 hover:text-[hsl(var(--foreground))] opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto group-focus-within:opacity-100 group-focus-within:pointer-events-auto [@media(hover:none)]:opacity-100 [@media(hover:none)]:pointer-events-auto focus:outline-none focus-visible:opacity-100 focus-visible:pointer-events-auto focus-visible:ring-2 focus-visible:ring-[#0F6E56]" aria-label={`More actions for bit ${b.marking_no || "no serial"}`}>
                                <MoreHorizontal className="w-4 h-4" />
                              </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="rl-frost border-[hsl(var(--border))]">
                              {canSell && b.status !== "sold" && (
                                <DropdownMenuItem onClick={() => setModal({ type: "sell", bit: b })}><Receipt className="w-3.5 h-3.5 mr-2" /> Mark sold</DropdownMenuItem>
                              )}
                              {canTransfer && b.status !== "sold" && b.status !== "in_transit" && (
                                <DropdownMenuItem onClick={() => setModal({ type: "transfer", bit: b })}><ArrowLeftRight className="w-3.5 h-3.5 mr-2" /> Transfer</DropdownMenuItem>
                              )}
                              <DropdownMenuItem onClick={() => duplicateBit(b)}><Copy className="w-3.5 h-3.5 mr-2" /> Duplicate</DropdownMenuItem>
                              <DropdownMenuItem onClick={() => exportCSV([b], `bit_${b.marking_no || b.id.slice(0, 6)}`)}><Download className="w-3.5 h-3.5 mr-2" /> Export row</DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {!loading && paged.length === 0 && (
                <tr><td colSpan={(canViewCost ? 10 : 9) - (showWarehouseCol ? 0 : 1)} className="px-3 py-14 text-center">
                  {(() => {
                    const wh = warehouses.find((w) => w.code === activeWh);
                    const isEmptyWarehouse = activeWh !== "ALL" && wh && (wh.count || 0) === 0;
                    const hasActiveFilters = q || statusFilter || reviewOnly || colFilters.blades || colFilters.thread_size;
                    if (isEmptyWarehouse) return (
                      <div className="flex flex-col items-center gap-2 text-[hsl(var(--muted-foreground))]">
                        <Package className="w-7 h-7 opacity-40" />
                        <span className="text-[14px] font-medium text-[hsl(var(--foreground))]">No stock in {wh.name} yet</span>
                        <span className="text-[12px]">Stock for this warehouse will appear here once it's added.</span>
                        {canManage && <button onClick={() => setModal({ type: "receive" })} className="mt-1 text-[12px] px-3 py-1.5 rounded-md text-white" style={{ backgroundColor: "#0F6E56" }}>Receive the first bit</button>}
                      </div>
                    );
                    if (hasActiveFilters) return (
                      <div className="flex flex-col items-center gap-2 text-[hsl(var(--muted-foreground))]">
                        <Search className="w-7 h-7 opacity-40" />
                        <span className="text-[14px] font-medium text-[hsl(var(--foreground))]">No bits match these filters</span>
                        <button onClick={() => { setQ(""); setStatusFilter(""); setReviewOnly(false); setColFilters({ blades: "", thread_size: "" }); }}
                                className="mt-1 text-[12px] px-3 py-1.5 rounded-md border border-[hsl(var(--border))]">Clear all filters</button>
                      </div>
                    );
                    return <span className="text-[13px] text-[hsl(var(--muted-foreground))]">No bits to show.</span>;
                  })()}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Mobile card list (<sm) — each bit is a tap-to-expand card: collapsed
            shows marking · size · type · status; expanding reveals details +
            actions. Replaces the wide table that doesn't fit a phone. */}
        <div className="sm:hidden">
          {loading ? (
            <div className="divide-y divide-[hsl(var(--border))]/60">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="flex items-center gap-3 px-4 py-3 animate-pulse" aria-hidden="true">
                  <span className="h-6 w-[72px] rounded-md bg-[hsl(var(--muted-foreground))]/15" />
                  <span className="h-3 flex-1 rounded bg-[hsl(var(--muted-foreground))]/12" style={{ maxWidth: `${40 + (i % 3) * 12}%` }} />
                  <span className="h-5 w-[68px] rounded-full bg-[hsl(var(--muted-foreground))]/15" />
                </div>
              ))}
            </div>
          ) : paged.length === 0 ? (
            <div className="px-4 py-14 text-center">
              {(() => {
                const wh = warehouses.find((w) => w.code === activeWh);
                const isEmptyWarehouse = activeWh !== "ALL" && wh && (wh.count || 0) === 0;
                const hasActiveFilters = q || statusFilter || reviewOnly || colFilters.blades || colFilters.thread_size;
                if (isEmptyWarehouse) return (
                  <div className="flex flex-col items-center gap-2 text-[hsl(var(--muted-foreground))]">
                    <Package className="w-7 h-7 opacity-40" />
                    <span className="text-[14px] font-medium text-[hsl(var(--foreground))]">No stock in {wh.name} yet</span>
                    {canManage && <button onClick={() => setModal({ type: "receive" })} className="mt-1 text-[12px] px-3 py-1.5 rounded-md text-white" style={{ backgroundColor: "#0F6E56" }}>Receive the first bit</button>}
                  </div>
                );
                if (hasActiveFilters) return (
                  <div className="flex flex-col items-center gap-2 text-[hsl(var(--muted-foreground))]">
                    <Search className="w-7 h-7 opacity-40" />
                    <span className="text-[14px] font-medium text-[hsl(var(--foreground))]">No bits match these filters</span>
                    <button onClick={() => { setQ(""); setStatusFilter(""); setReviewOnly(false); setColFilters({ blades: "", thread_size: "" }); }}
                            className="mt-1 text-[12px] px-3 py-1.5 rounded-md border border-[hsl(var(--border))]">Clear all filters</button>
                  </div>
                );
                return <span className="text-[13px] text-[hsl(var(--muted-foreground))]">No bits to show.</span>;
              })()}
            </div>
          ) : (
            <div className="divide-y divide-[hsl(var(--border))]/60">
              {paged.map((b) => (
                <BitCardMobile key={b.id} b={b} canViewCost={canViewCost} canSell={canSell} canManage={canManage}
                  showWarehouse={showWarehouseCol}
                  onDetail={() => setModal({ type: "detail", bit: b })}
                  onSell={() => setModal({ type: "sell", bit: b })}
                  onDuplicate={() => duplicateBit(b)}
                  onExportRow={() => exportCSV([b], `bit_${b.marking_no || b.id.slice(0, 6)}`)} />
              ))}
            </div>
          )}
        </div>

        {/* Footer / pagination */}
        <div className="flex items-center justify-between px-4 py-2.5 text-[12px] text-[hsl(var(--muted-foreground))] border-t border-[hsl(var(--border))] flex-wrap gap-2">
          <span>Showing <span className="font-mono-data font-semibold text-[hsl(var(--foreground))]">{paged.length ? (pageClamped - 1) * pageSize + 1 : 0}–{(pageClamped - 1) * pageSize + paged.length}</span> of <span className="font-mono-data font-semibold text-[hsl(var(--foreground))]">{filtered.length.toLocaleString()}</span></span>
          <div className="flex items-center gap-1.5">
            <div className="inline-flex rounded-md overflow-hidden border border-[hsl(var(--border))] mr-1">
              {PAGE_SIZES.map((n) => (
                <button key={n} onClick={() => { setPageSize(n); setPage(1); }}
                  className={`px-2 py-0.5 text-[10px] font-mono-data transition-colors ${pageSize === n ? "bg-[hsl(var(--foreground))] text-[hsl(var(--background))] font-semibold" : "bg-[hsl(var(--muted))]/40 hover:bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))]"}`}>{n}</button>
              ))}
            </div>
            <button disabled={pageClamped <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} className="w-6 h-6 inline-flex items-center justify-center rounded border border-[hsl(var(--border))] bg-[hsl(var(--muted))]/30 hover:bg-[hsl(var(--muted))] disabled:opacity-40"><ChevronLeft className="w-3.5 h-3.5" /></button>
            <span className="font-mono-data tabular-nums">{pageClamped}/{totalPages}</span>
            <button disabled={pageClamped >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} className="w-6 h-6 inline-flex items-center justify-center rounded border border-[hsl(var(--border))] bg-[hsl(var(--muted))]/30 hover:bg-[hsl(var(--muted))] disabled:opacity-40"><ChevronRight className="w-3.5 h-3.5" /></button>
          </div>
        </div>
        </>}
      </div>

      {modal?.type === "receive" && (
        <ReceiveBitModal defaultWarehouse={activeWh} onClose={() => setModal(null)}
          onDone={() => { fetchBits(); fetchWarehouses(); fetchValueSummary(); }} />
      )}
      {modal?.type === "import" && (
        <BitImportModal onClose={() => setModal(null)}
          onDone={() => { fetchBits(); fetchWarehouses(); fetchValueSummary(); }} />
      )}
      {modal?.type === "sell" && (
        <SellBitModal bit={modal.bit} onClose={() => setModal(null)}
          onDone={() => { fetchBits(); fetchValueSummary(); }} />
      )}
      {modal?.type === "transfer" && (
        <TransferBitModal bit={modal.bit} onClose={() => setModal(null)}
          onDone={() => { fetchBits(); fetchWarehouses(); fetchValueSummary(); }} />
      )}
      {modal?.type === "detail" && (
        <BitDetailModal bit={modal.bit} canManage={canManage} canViewCost={canViewCost} suggestions={suggestions}
          onClose={() => setModal(null)}
          onSaved={() => { fetchBits(); fetchValueSummary(); }} />
      )}
    </div>
  );
}
